'use client';

import { cn } from '@bells/core/lib/utils';
import { Button } from '@bells/ui/components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from '@bells/ui/components/command';
import { endOfDay, isBefore } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';

import { format, startOfDay } from 'date-fns';

import { locationSearchChange, locationSearchWhenDesktop } from '@bells/i18n';
import { Calendar } from '@bells/ui/components/calendar';
import { CommandItem } from '@bells/ui/components/command';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@bells/ui/components/drawer';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@bells/ui/components/popover';
import { useMediaQuery } from '@bells/ui/hooks/use-media-query';
import { CalendarIcon, Check, Clock } from 'lucide-react';
import { formatDateIntl, formatDateOnly, formatTimeOnly } from './utils';
const hoursInFuture = 3;

const is24HourFormat = () => {
  const formatter = new Intl.DateTimeFormat(undefined, { hour: 'numeric' });
  const formattedTime = formatter.format(new Date());
  return !formattedTime.includes('AM') && !formattedTime.includes('PM');
};

export const correctDateTime = (initDate: Date | string) => {
  const date = typeof initDate === 'string' ? new Date(initDate) : initDate;
  date.setSeconds(0);
  date.setMilliseconds(0);

  if (date.getMinutes() > 30 && date.getMinutes() < 60) {
    date.setHours(date.getHours() + 1);
    date.setMinutes(0);
  } else if (date.getMinutes() > 0 && date.getMinutes() < 30) {
    date.setMinutes(30);
  }

  const startRestricted = 1 * 60 + 30;
  const endRestricted = 8 * 60;

  const now = new Date();
  const twoHoursLater = new Date(
    now.getTime() + hoursInFuture * 60 * 60 * 1000
  );

  while (true) {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const totalMinutes = hours * 60 + minutes;

    if (
      date >= twoHoursLater &&
      !(totalMinutes >= startRestricted && totalMinutes < endRestricted)
    ) {
      return date;
    }

    date.setMinutes(date.getMinutes() + 30);

    if (date.getMinutes() === 60) {
      date.setMinutes(0);
      date.setHours(date.getHours() + 1);
    }
  }
};

const SelectionTimeList = ({
  disabled,
  notFoundLabel = 'Keine Uhrzeit gefunden',
  currentDate,
  setCurrentDate,
  is24Hours,
  startDate,
  className,
}: {
  disabled?: boolean;
  notFoundLabel?: string;
  currentDate?: Date;
  setCurrentDate?: (val: Date) => void;
  is24Hours: boolean;
  startDate: Date;
  className?: string;
}) => {
  const [search, setSearch] = useState('');

  const timeArray = useMemo(() => {
    if (!currentDate) {
      return [];
    }

    const times: Date[] = [];
    const origDate = new Date(currentDate);
    const startTime =
      startOfDay(origDate) < new Date(startDate)
        ? new Date(startDate)
        : startOfDay(origDate);
    const endTime = endOfDay(startTime);
    while (isBefore(startTime, endTime)) {
      const formattedTime = new Date(startTime);
      const hours = startTime.getHours();
      const minutes = startTime.getMinutes();

      if (!(hours >= 1 && hours < 8) || (hours === 1 && minutes < 30)) {
        times.push(formattedTime);
      }

      startTime.setTime(startTime.getTime() + 30 * 60 * 1000);
    }

    return times;
  }, [is24Hours, currentDate]);

  return (
    <Command className='bg-transparent'>
      <CommandInput value={search} onValueChange={setSearch} />
      <CommandList className={cn(className, 'bg-transparent')}>
        <CommandEmpty className='p-4'>{notFoundLabel}</CommandEmpty>
        <CommandGroup>
          {timeArray.map((item, index) => (
            <CommandItem
              key={`time-${index}`}
              disabled={disabled}
              value={item.toISOString()}
              onSelect={() => {
                setCurrentDate?.(item);
              }}
            >
              <div className='flex w-full items-center gap-2'>
                <span>{format(item, is24Hours ? 'HH:mm' : 'hh:mm')}</span>
                <span className='text-muted-foreground'>
                  {is24Hours ? 'Uhr' : format(item, 'aa')}
                </span>
              </div>
              <Check
                className={cn(
                  'ml-auto',
                  item?.toString() === currentDate?.toString()
                    ? 'opacity-100'
                    : 'opacity-0'
                )}
              ></Check>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
};

type DatePickerInternalProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange' | 'onSelect' | 'onDurationChange'
> & {
  value?: Date;
  startDate: Date;
  onDateChange: (val: Date | undefined) => void;
  is24Hours: boolean;
  handleDateSelect: (date: Date | undefined) => void;
};

const DatePickerDesktop = React.forwardRef<
  HTMLDivElement,
  DatePickerInternalProps
>(({ value, startDate, onDateChange, is24Hours, handleDateSelect }, ref) => {
  const [open, setOpen] = useState(false);
  const [openTime, setOpenTime] = useState(false);

  return (
    <div
      className='inline-flex w-full items-center gap-4 rounded-lg border border-input'
      ref={ref}
    >
      <Popover modal open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type='button'
            variant={'ghost'}
            className={cn(
              'w-auto break-all border-0 px-2 py-4 pl-3 text-left text-foreground ring-0 hover:ring-0 focus:ring-0 focus-visible:ring-0',
              !value && 'text-muted-foreground'
            )}
          >
            {value ? formatDateOnly(value) : <span>Datum wählen</span>}
            <CalendarIcon className='ml-auto h-4 w-4' />
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-0'>
          <Calendar
            startMonth={startDate}
            disabled={{
              before: startDate,
            }}
            defaultMonth={value ?? startDate}
            showOutsideDays
            id='dateWhen'
            className='w-auto border-0 px-2 py-4 text-foreground ring-0 hover:ring-0 focus:ring-0 focus-visible:ring-0'
            mode='single'
            selected={value}
            onSelect={(val) => {
              onDateChange(val);
              setOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      <Popover modal open={openTime} onOpenChange={setOpenTime}>
        <PopoverTrigger asChild>
          <Button
            type='button'
            variant={'ghost'}
            className={cn(
              'w-auto break-all border-0 px-2 py-4 pl-3 text-left text-foreground ring-0 hover:ring-0 focus:ring-0 focus-visible:ring-0',
              !value && 'text-muted-foreground'
            )}
          >
            {value ? formatTimeOnly(value) : <span>Uhrzeit wählen</span>}
            <Clock className='ml-auto h-4 w-4' />
          </Button>
        </PopoverTrigger>
        <PopoverContent className='max-w-[30rem] p-0' align='start'>
          <SelectionTimeList
            className='max-h-[200px] sm:max-h-[300px]'
            startDate={startDate}
            is24Hours={is24Hours}
            setCurrentDate={(val) => {
              handleDateSelect(val);
              setOpenTime(false);
            }}
            currentDate={value}
          ></SelectionTimeList>
        </PopoverContent>
      </Popover>
    </div>
  );
});

DatePickerDesktop.displayName = 'DatePickerDesktop';

const DatePickerMobile = React.forwardRef<
  HTMLDivElement,
  DatePickerInternalProps
>(({ value, startDate, onDateChange, is24Hours, handleDateSelect }, ref) => {
  const [open, setOpen] = useState(false);

  return (
    <div className='flex w-full flex-col gap-1'>
      <Drawer repositionInputs={false} open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>
          <Button
            type='button'
            variant={'outline'}
            className={cn(
              'flex h-auto w-full max-w-full items-center justify-between gap-2 overflow-hidden px-2 text-left'
            )}
          >
            <span
              className={cn('line-clamp-1 flex-1 whitespace-normal', {
                'text-muted-foreground': !value,
              })}
            >
              {value ? formatDateIntl(value) : <span>Datum wählen</span>}
            </span>
            <CalendarIcon className='ml-auto h-4 w-4' />

            {!value && (
              <span className='relative flex size-3'>
                <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-pink-400 opacity-75'></span>
                <span className='relative inline-flex size-3 rounded-full bg-pink-500'></span>
              </span>
            )}
          </Button>
        </DrawerTrigger>
        <DrawerContent className='h-full max-h-[100vh]'>
          <DrawerHeader className='text-left'>
            <DrawerTitle>{locationSearchWhenDesktop()}</DrawerTitle>
          </DrawerHeader>
          <div className='relative flex w-full flex-1 flex-col'>
            <div className='flex-1'>
              <Calendar
                defaultMonth={value ?? startDate}
                startMonth={startDate}
                disabled={{
                  before: startDate,
                }}
                id='dateWhen'
                className='w-auto border-0 px-2 py-0 text-foreground ring-0 hover:ring-0 focus:ring-0 focus-visible:ring-0'
                mode='single'
                selected={value}
                onSelect={onDateChange}
                initialFocus
              />
            </div>

            <div className='relative h-full w-full overflow-hidden'>
              <div className='absolute h-full w-full overflow-auto'>
                <SelectionTimeList
                  className='h-full max-h-full'
                  startDate={startDate}
                  is24Hours={is24Hours}
                  setCurrentDate={handleDateSelect}
                  currentDate={value}
                ></SelectionTimeList>
              </div>
            </div>
          </div>
          <DrawerFooter className='pt-2'>
            <DrawerClose asChild>
              <Button variant={'default'} type='button'>
                {locationSearchChange()}
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
});

DatePickerMobile.displayName = 'DatePickerMobile';

const DateTimePicker = React.forwardRef<
  HTMLDivElement,
  Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> & {
    onChange?: (val?: Date) => void;
    initDate?: Date;
  }
>(({ onChange, initDate }, ref) => {
  const startDate = useMemo(() => {
    return correctDateTime(new Date());
  }, []);

  useEffect(() => {
    if (initDate) {
      setValue(correctDateTime(initDate));
    }
  }, [initDate]);

  const [value, setValue] = useState<Date | undefined>(startDate);

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setValue(date);
      onChange?.(date);
    }
  };

  const onDateChange = (val: Date | undefined) => {
    const newDate = new Date(val ?? startDate);

    newDate.setSeconds(0);
    if (newDate <= startDate) {
      handleDateSelect(startDate);
      onChange?.(startDate);
    } else {
      if (value) {
        newDate.setHours(value.getHours());
        newDate.setMinutes(value.getMinutes());
        newDate.setSeconds(0);
      }
      handleDateSelect(newDate);
      onChange?.(newDate);
    }
  };

  const is24Hours = is24HourFormat();
  const isDesktop = useMediaQuery('(min-width: 768px)');

  return isDesktop ? (
    <DatePickerDesktop
      ref={ref}
      startDate={startDate}
      onDateChange={onDateChange}
      is24Hours={is24Hours}
      handleDateSelect={handleDateSelect}
      value={value}
    ></DatePickerDesktop>
  ) : (
    <DatePickerMobile
      ref={ref}
      startDate={startDate}
      onDateChange={onDateChange}
      is24Hours={is24Hours}
      handleDateSelect={handleDateSelect}
      value={value}
    ></DatePickerMobile>
  );
});
DateTimePicker.displayName = 'DateTimePicker';

export { DateTimePicker };
